import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { Redirect, useHistory } from "react-router-dom";
import {
  Box,
  Button,
  Flex,
  Grid,
  Text,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { ChevronLeftIcon, CloseIcon } from "@chakra-ui/icons";
import { useTranslation, Trans } from "react-i18next";
import { DateTime } from "luxon";
import { LOCK_DURATION_SECONDS } from "../../App";
import { availApiClient } from "../../apiConfig";
import { _basePath } from "../../config";
import { useLock, UserContext } from "../../context";

import MotionDiv from "../../common/MotionDiv";
import { useApiTenant } from "../../context/ApiAuthContext";
import Loader from "../../common/Loader";

const TimeSelection = ({
  event,
  treeType = "Tenant",
  showIsNotAvailable = true,
}) => {
  const { clearLock } = useLock();
  const { tenantId } = useApiTenant();
  const { userData, setUserData } = React.useContext(UserContext);
  const { t } = useTranslation();
  const [selected, setSelected] = useState(null);
  let history = useHistory();
  const queryClient = useQueryClient();
  const toast = useToast();
  const now = DateTime.now();
  let subCategory = "Events";
  if (event.children && event.children.length > 0) {
    subCategory = "ElementTree";
  }
  function onCancel() {
    clearLock().then(() => {
      setUserData({
        user: null,
        loggedIn: false,
        event: null,
      });
      history.push(_basePath);
    });
  }
  const req = {
    startTime: new Date(now.plus({ seconds: 30 })).toISOString(),
    endTime: new Date(now.plus({ hours: 2 })).toISOString(),
    resources: [
      {
        id: userData?.user?.id,
        partitionKey: userData?.user?.partitionKey,
        category: userData?.user?.category,
        subCategory: userData?.user?.subCategory,
        name: userData?.user?.name,
      },
    ],
  };

  const { isLoading, isError, data, error } = useQuery(
    ["availability", event.id],
    () =>
      availApiClient.userApiAvailabilityGetTimeSlotsAvailableIdPost(
        event.id,
        treeType,
        showIsNotAvailable,
        tenantId,
        "1.0",
        "en-US,en",
        req
      )
  );

  const { mutate, isLoading: isLocking } = useMutation(
    (lockReq) =>
      availApiClient.userApiAvailabilityTryCreateLockPost(
        tenantId,
        "1.0",
        "en-US,en",
        lockReq
      ),
    {
      onSuccess: (res, variables) => {
        // Store the lock information in user context
        const lockData = {
          ...variables,
          scheduleId: res?.data?.data?.scheduleId,
          rules: res?.data?.data?.rules || null,
        };
        setUserData({
          ...userData,
          lock: lockData,
        });

        // Now evaluate rules to check if document signing is required
        history.push(`${_basePath}/appointment/schedule`);
      },
      onError: () => {
        queryClient.invalidateQueries(["availability", event.id]);
        setSelected(null);
        toast({
          title: t(
            "error.no_available_appointments",
            `There are no available appointments for that selection.`
          ),
          description: t(
            "error.try_another_selection",
            "Please try another selection."
          ),
          status: "error",
          duration: 2500,
          isClosable: true,
        });
      },
    }
  );

  const setSelectedTime = (schedule) => {
    setSelected(schedule);
  };
  const requestLock = () => {
    toast.closeAll();
    const lockReq = {
      name: event?.name,
      startTime: selected.startTime,
      endTime: selected.endTime,
      lockDurationInSeconds: LOCK_DURATION_SECONDS,
      subCategory: "Events",
      creationLocation: "Kiosk",
      resources: [
        {
          id: userData?.user?.id,
          partitionKey: userData?.user?.partitionKey,
          category: userData?.user?.category,
          subCategory: userData?.user?.subCategory,
          name: userData?.user?.name,
        },
        {
          slots: event.slots,
          id: event?.id,
          partitionKey: event?.partitionKey,
          category: event?.category,
          subCategory: event?.subCategory,
          name: event?.name,
        },
      ],
    };
    mutate(lockReq);
  };

  if (!userData?.loggedIn) {
    return <Redirect to={_basePath} />;
  }
  if (isLoading) {
    return (
      <Flex w={"full"} h={"full"}>
        <Loader
          text={
            isLoading &&
            t("loader.checking_availability", "Checking Availability...")
          }
        />
      </Flex>
    );
  }

  return (
    <VStack
      color={"#fff"}
      direction={"column"}
      alignItems={"space-between"}
      flex={1}
      h={"full"}
      // spacing="24px"
      spacing={5}
    >
      <Text
        as={"h2"}
        color="brand.secondary.500"
        fontSize="2xl"
        textAlign={"left"}
        fontWeight={"bold"}
      >
        <Trans i18nKey={"new_appointment"}>New Appointment:</Trans>{" "}
        <Trans i18nKey={`${subCategory}.${event?.name}`} ns={subCategory}>
          {event?.name}
        </Trans>
      </Text>
      <Text as="p" textAlign={"left"}>
        <Trans i18nKey={"select_a_time"}>
          Select a time option below to reserve your appointment.
        </Trans>
        <br />
        <Trans i18nKey={"note_availability"}>
          NOTE: Time options are based on availability and may not always be
          always be available.
        </Trans>
      </Text>
      <Flex
        // m={30}
        flex={1}
        w={"full"}
        direction={"column"}
        // px={[10, 15, 20, 25, 30]}
        overflow={"hidden"}
        position={"relative"}
        // gap={4}
        // paddingTop={8}
        height={"full"}
        overflowY={"auto"}
      >
        <Grid
          gridGap={{ sm: 4, md: 6, lg: 8, xl: 10 }}
          w={"full"}
          // h={"full"}
          // templateRows={"minmax(0,60px)"}
          templateColumns={{
            sm: "repeat(2, 1fr)",
            md: "repeat(3, 1fr)",
            lg: "repeat(4, 1fr)",
          }}

          // px={8}
        >
          {data?.data?.data?.map((schedule, i) => {
            const time = DateTime.fromISO(schedule.startTime).toFormat("t");
            return (
              <Box
                onClick={() =>
                  schedule?.isAvailable && setSelectedTime(schedule)
                }
                key={schedule.startTime}
                data-testid={`time-option-${i}`}
                position="relative"
              >
                <Flex
                  h={"full"}
                  minHeight={"100px"}
                  cursor="pointer"
                  bg={
                    selected?.startTime === schedule.startTime
                      ? "rgba(147, 112, 219, 0.3)"
                      : "rgba(0,0,0,0.2)"
                  }
                  borderRadius={"md"}
                  textAlign={"center"}
                  justifyContent={"center"}
                  flexDirection={"column"}
                  alignItems={"center"}
                  opacity={schedule.isAvailable ? 1 : 0.5}
                  border={
                    selected?.startTime === schedule.startTime
                      ? "6px solid #FFD700"
                      : "6px solid transparent"
                  }
                  color={
                    selected?.startTime === schedule.startTime
                      ? "white"
                      : schedule.isAvailable
                      ? "brand.secondary.500"
                      : "brand.primary.500"
                  }
                >
                  <Text
                    fontSize={{ sm: "1xl", md: "2xl", lg: "3xl" }}
                    fontWeight={
                      selected?.startTime === schedule.startTime
                        ? "bold"
                        : "normal"
                    }
                  >
                    {time}
                  </Text>
                </Flex>
              </Box>
            );
          })}
        </Grid>
      </Flex>
      <Flex
        w="full"
        justify="space-between"
        align="center"
        // px={8}
        pt={4}
        position="relative"
      >
        <Button
          justifySelf={"start"}
          variant={"kioskGhost"}
          aria-label={"Back"}
          color="white"
          leftIcon={<ChevronLeftIcon boxSize={6} />}
          onClick={() => history.goBack()}
          fontSize="lg"
          _hover={{ bg: "rgba(255, 255, 255, 0.1)" }}
          data-testid={"back-button"}
        >
          <Trans i18nKey={"actions.back"}>Back</Trans>
        </Button>

        <Button
          isDisabled={isLocking}
          onClick={requestLock}
          bg={"brand.secondary.500"}
          color={"brand.primary.500"}
          size="lg"
          px={8}
          fontSize="lg"
          fontWeight="semibold"
          data-testid={"confirm-appointment"}
          visibility={selected ? "visible" : "hidden"}
        >
          {isLocking && selected
            ? t("validating", "Validating...")
            : t("actions.confirm", "Confirm")}
        </Button>

        <Button
          justifySelf={"end"}
          variant={"kioskGhost"}
          aria-label={"cancel"}
          color="white"
          leftIcon={<CloseIcon boxSize={4} />}
          onClick={onCancel}
          fontSize="lg"
          _hover={{ bg: "rgba(255, 255, 255, 0.1)" }}
          data-testid={"cancel-button"}
        >
          <Trans i18nKey={"actions.cancel"}>Cancel</Trans>
        </Button>
      </Flex>
    </VStack>
  );
};

export default TimeSelection;
